import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import FractionInput, { formatLatexPreview } from '../../../../components/inputs/FractionInput';

// ── helpers ──────────────────────────────────────────
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const powi = (a, k) => (k >= 0 ? Math.pow(a, k) : 1 / Math.pow(a, -k));
const isIntegerOrProperFraction = (value) => Number.isInteger(value) || (Math.abs(value) > 0 && Math.abs(value) < 1);

// „log” z ukrytą podstawą 10
const logb = (a) => (a === 10 ? '\\log' : `\\log_{${a}}`);

// NWD + uproszczenia ułamków
const gcd = (a, b) => { a = Math.abs(a); b = Math.abs(b); while (b) [a, b] = [b, a % b]; return a || 1; };
function simplifyFrac(num, den) {
  if (den < 0) { num = -num; den = -den; }
  const g = gcd(num, den);
  return { num: num / g, den: den / g };
}

// log z podstawą wypisaną wprost (dla wyjaśnień od kroku 2)
const logExp = (a) => (a === 10 ? '\\log_{10}' : `\\log_{${a}}`);


// aproksymacja liczby rzeczywistej ułamkiem (metoda ułamków łańcuchowych)
function toFraction(x, maxDen = 10000) {
  const sign = x < 0 ? -1 : 1;
  x = Math.abs(x);
  if (!isFinite(x)) return { num: sign, den: 0 };
  let h0 = 0, k0 = 1, h1 = 1, k1 = 0;
  let y = x;
  for (let i = 0; i < 20; i++) {
    const a = Math.floor(y);
    const h2 = a * h1 + h0;
    const k2 = a * k1 + k0;
    if (k2 > maxDen) break;
    h0 = h1; k0 = k1; h1 = h2; k1 = k2;
    const frac = y - a;
    if (frac < 1e-12) break;
    y = 1 / frac;
  }
  let num = h1, den = k1;
  if (k0 !== 0 && Math.abs(h0 / k0 - x) < Math.abs(num / den - x)) {
    num = h0; den = k0;
  }
  ({ num, den } = simplifyFrac(num * sign, den));
  return { num, den };
}

// zapis „ładnej liczby” jako LaTeX: całkowita / zwykła / mieszana
function niceLatexFromNumber(val) {
  if (!isFinite(val)) return String(val);
  const int = Math.round(val);
  if (Math.abs(val - int) < 1e-12) return String(int);

  let { num, den } = toFraction(val);
  ({ num, den } = simplifyFrac(num, den));
  const sgn = num < 0 ? "-" : "";
  num = Math.abs(num);

  return `${sgn}\\tfrac{${num}}{${den}}`;
}

// czy c jest „czystą potęgą” a (dla małych wykładników)
function isPowerOfBase(c, a) {
  for (let k = -6; k <= 6; k++) {
    if (Math.abs(c - Math.pow(a, k)) < 1e-10) return true;
  }
  return false;
}

// „ładne” mnożniki – tak dobieramy X,Y, by były prosto zapisywalne w ułamkach zwykłych/mieszanych
const NICE_BY_BASE = {
  10: [2, 4, 5, 8, 25, 50, 1/2, 1/4, 4/5, 20, 1/5],
   5: [2, 4, 8, 10, 1/2, 1/4],
   4: [2, 8, 1/2, 10, 1/4, 2/5, 5],
   3: [2, 6, 1/2, 3/4, 12, 1/4],
   2: [3, 6, 3/4, 5, 10, 1/5, 2/5],
};

// wybiera parę (X, Y) tak, aby X·Y = A^t oraz X,Y nie były czystymi potęgami A
function splitIntoNiceFactors(a, t) {
  const P = powi(a, t);
  const pool = (NICE_BY_BASE[a] || NICE_BY_BASE[10]).filter(c => c !== 1 && !isPowerOfBase(c, a));

  const exps = t >= 0 ? Array.from({ length: t + 1 }, (_, i) => i)
                      : Array.from({ length: -t + 1 }, (_, i) => t + i);

  for (let tries = 0; tries < 40; tries++) {
    const u = pick(exps);
    const c = pick(pool);
    const X = c * powi(a, u);
    const Y = P / X;

    const nearIntX = Math.abs(X - Math.round(X)) < 1e-12;
    const nearIntY = Math.abs(Y - Math.round(Y)) < 1e-12;
    const xIsPure = nearIntX && isPowerOfBase(Math.round(X), a);
    const yIsPure = nearIntY && isPowerOfBase(Math.round(Y), a);
    if (!xIsPure && !yIsPure && isFinite(Y) && isIntegerOrProperFraction(X) && isIntegerOrProperFraction(Y)) {
      return { X, Y, Xs: niceLatexFromNumber(X), Ys: niceLatexFromNumber(Y) };
    }
  }

  const c = a === 2 ? 3 : 2;
  const u = t > 0 ? 1 : 0;
  const X = c * powi(a, u);
  const Y = P / X;
  return { X, Y, Xs: niceLatexFromNumber(X), Ys: niceLatexFromNumber(Y) };
}

// ── generator ────────────────────────────────────────
export function generateTask() {
  const a = pick([2, 3, 4, 5, 10]);               // podstawa a>0, a≠1
  const t = pick([-3, -2, -1, 1, 2, 3, 4]);       // wynik
  const { X, Y, Xs, Ys } = splitIntoNiceFactors(a, t);
  return {
    type: 'LogPropSumTask',
    data: { a, t, X, Y, Xs, Ys }
  };
}

export function renderTask({ data }) {
  const { a, Xs, Ys } = data;
  const expr = `${logb(a)} ${Xs} + ${logb(a)} ${Ys}`;
  return (
    <div>
      Oblicz wartość wyrażenia:
      <div
        style={{
          marginTop: 14,
          marginBottom: 14,
          fontSize: "16px",
          lineHeight: 1.5
        }}
      >
        <BlockMath math={expr} />
      </div>
    </div>
  );
}

// --- renderInput (z doklejaniem _labelLatex) ---
export function renderInput(task, userAnswer, onChange, disabled) {
  const handleChange = (ans) => {
    // nie dokładamy już żadnego _labelLatex – przekazujemy tylko to,
    // co wpisuje użytkownik
    onChange && onChange(ans);
  };

  return (
    <div className="space-y-2">
      <FractionInput
        value={userAnswer}
        onChange={handleChange}
        disabled={disabled}
        label=""  
      />
    </div>
  );
}


// --- renderUserAnswer (tylko odpowiedź, bez „=” i lewej strony) ---
export function renderUserAnswer(userAnswer, task) {
  if (!userAnswer || typeof userAnswer !== 'object' || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }

  return <InlineMath math={formatLatexPreview(userAnswer.raw)} />;
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== 'object') return false;
  const given = userAnswer.parsed;
  if (given == null) return false;
  return Math.abs(given - data.t) < 1e-4;
}

// --- renderAnswer (tylko wynik, bez lewej strony i znaku równości) ---
export function renderAnswer({ data }) {
  const { t } = data;
  return <InlineMath math={String(t)} />;
}

export function renderExplanation({ data }) {
  const { a, Xs, Ys, t, X, Y } = data;
  const prodLatex = niceLatexFromNumber(X * Y);

  return (
    <div className="space-y-2">
      <p><strong>Krok 1:</strong> Własność sumy:</p>
      <BlockMath math={'\\log_{a} x + \\log_{a} y = \\log_{a}(xy)'} />
      {a === 10 && (
        // Uwaga w jednej linijce, z inline-math (żadnych BlockMath tutaj)
        <p style={{ marginTop: 6 }}>
          <em>Uwaga:</em> zapis <InlineMath math={"\\log"} /> oznacza logarytm dziesiętny,
          czyli <InlineMath math={"\\log x = \log_{10} x"} />.
        </p>
      )}

      <p><strong>Krok 2:</strong> Podstawiamy i mnożymy:</p>
      <BlockMath math={`${logExp(a)} ${Xs} + ${logExp(a)} ${Ys} = ${logExp(a)}\\!\\left(${Xs}\\cdot${Ys}\\right) = ${logExp(a)} ${prodLatex}`} />

      <p><strong>Krok 3:</strong> Obliczamy:</p>
      <BlockMath math={`${logExp(a)} ${prodLatex} = ${t}`} />

      <p><strong>Wniosek:</strong></p>
      <BlockMath math={`${logExp(a)} ${Xs} + ${logExp(a)} ${Ys} = ${t}`} />
    </div>
  );
}
